import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import {
  useTable,
  useRowSelect,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
} from "react-table";
import {
  Button,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import {Link, Link as RouterLink} from "react-router-dom";
import { Create, Delete } from "@material-ui/icons";
import apiHelper from "../../../../util/ApiHelper";
import { DatePicker } from "@material-ui/pickers";
import { toast } from "react-toastify";
import { formatDatetime, CustomCheckbox, modal } from "../../../../util";
import PaginationBar from "../../../common/PaginationBar";
import { useLoading } from "../../../../util/LoadingProvider";

const FilterDiv = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 20px;

  p {
    font-size: 13px;
    line-height: 15px;
    color: #306f9d;
    padding-left: 3px;
    padding-right: 3px;
    cursor: pointer;

    span {
      color: #333333;
    }

    &.active {
      font-weight: bold;
      color: #333333;
    }

    &:first-child {
      padding-left: 0;
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  input: {
    width: "100%",
  },
  margin: {
    marginBottom: "40px",
  },
  flexRow: {
    display: "flex",
    justifyContent: "space-between",
    "& .MuiSelect-root": {
      paddingTop: "9px",
      paddingBottom: "9px",
    },
    "& input": {
      padding: "11px 14px",
    },
    "& label": {
      transform: "translate(14px, 12px) scale(1)",
    },
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
}));

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <CustomCheckbox color="primary" ref={resolvedRef} {...rest} />
      </>
    );
  }
);

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <>
      <TextField
        id="search-field"
        label="Search News"
        variant="outlined"
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
        }}
      />
      <Button
        type="submit"
        variant="outlined"
        color="primary"
        onClick={() => {
          onChange(value);
        }}
      >
        Search
      </Button>
    </>
  );
}

export default function NewsListingPage({ disabled, ...props }) {
  const { setLoading } = useLoading();
  const classes = useStyles();
  const [data, setData] = React.useState([]);
  const [allData, setAllData] = React.useState([]);
  const [filterActive, setFilterActive] = React.useState([]);
  const [filterInactive, setFilterInactive] = React.useState([]);
  const [filterStatus, setFilterStatus] = React.useState("all");
  const [filterType, setFilterType] = React.useState("");

  const [bulkActions, setBulkActions] = React.useState("");
  const [selectedDate, handleDateChange] = React.useState(null);

  // Define initial state variables for pagination
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [globalFilter, setGlobalFilterValue] = React.useState('');

  //for storing total count
  const [totalCount, setTotalCount] = React.useState(0);

  const getNews = React.useCallback(async (status = "", type = "") => {
    setLoading(true);
    try {
      const typeParam = type || filterType;
      const response = await apiHelper.hkiff.getHkiffNews(
        typeParam,
        status,
        pageIndex,
        pageIndex * pageSize,
        pageSize,
        globalFilter
      );
      
      setData(response.data.content);
      setTotalCount(response.data.totalElements);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, globalFilter, filterType]);

  const getFilterCounts = React.useCallback(async () => {
    try {
      const response = await apiHelper.hkiff.getHkiffNews(
        "",
        "",
        0, 
        0, 
        999999,
        "" 
      );
      
      setAllData(response.data.content);
      setFilterActive(response.data.content.filter(x => x.status === 1));
      setFilterInactive(response.data.content.filter(x => x.status === 0));
    } catch (error) {
      console.error("Error fetching filter counts:", error);
    }
  }, []);

  const actions = [
    {
      value: "delete",
      label: "Delete",
    },
  ];

  const handleBulkActions = (event, actions) => {
    setBulkActions(event.target.value);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Title",
        accessor: "localizations.en.title",
      },
      {
        Header: "Type",
        accessor: "type",
      },
      {
        Header: "Published Time",
        accessor: "publishTime",
      },
      {
        Header: "Status",
        accessor: "status",
      },
      {
        Header: "Last Update",
        accessor: "updateTime",
      },
      {
        Header: "Action",
        accessor: "",
      },
    ],
    [data]
  );

  const {
    getTableProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    preGlobalFilteredRows,
    setGlobalFilter,
    selectedFlatRows,
    state,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / pageSize),
      autoResetPage: false,
      useControlledState: (state) => {
        return React.useMemo(
          () => ({
            ...state,
            pageIndex,
            pageSize,
          }),
          [state, pageIndex, pageSize]
        );
      },
    },
    useGlobalFilter,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox
                {...{ ...getToggleAllRowsSelectedProps(), disabled }}
              />
            </div>
          ),
        },
        ...columns,
      ]);
    }
  );

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
  };
  
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(Number(newPageSize));
    setPageIndex(0);
  };
  
  const customGotoPage = (page) => {
    handlePageChange(page);
  };
  
  const customNextPage = () => {
    if (canNextPage) {
      handlePageChange(pageIndex + 1);
    }
  };
  
  const customPreviousPage = () => {
    if (canPreviousPage) {
      handlePageChange(pageIndex - 1);
    }
  };
  
  const customSetPageSize = (size) => {
    handlePageSizeChange(size);
  };

  const deletePage = async (id) => {
    const decision = await modal.fire({
      title: "Delete News",
      text: "Are you sure to delete this news?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteHkiffNewsById(id);
        if (result.data.code === 0) {
          getNews(
            filterStatus === "active" ? "1" : 
            filterStatus === "inactive" ? "0" : "",
            filterType
          );
          getFilterCounts();
          toast.dark("News deleted");
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const applyBulkAction = async () => {
    if (bulkActions === "delete") {
      const arg = [];
      selectedFlatRows.map((row) => {
        arg.push(apiHelper.hkiff.deleteHkiffNewsById(row.original.id));
      });

      const result = await Promise.all(arg);
      const sum = result
        .map((o) => o.data.code)
        .reduce((accumulator, currentValue) => accumulator + currentValue);

      if (sum === 0) {
        getNews(
          filterStatus === "active" ? "1" : 
          filterStatus === "inactive" ? "0" : "",
          filterType
        );
        getFilterCounts();
        toast.dark(`${result.length} news deleted.`);
      }
    }
  };

  const filterByType = (type) => {
    setFilterType(type);
    setPageIndex(0);
    getNews(
      filterStatus === "active" ? "1" :
      filterStatus === "inactive" ? "0" : "",
      type
    );
  };

  const filterDataWithStatus = (status) => {
    setFilterStatus(status);
    setFilterType("");

    switch (status) {
      case "active":
        getNews("1", ""); // Active status = "1"
        break;

      case "inactive":
        getNews("0", ""); // Inactive status = "0"
        break;

      case "all":
      default:
        getNews("", ""); // No status filter
        break;
    }
  };

  // Initial load
  useEffect(() => {
    getNews();

    getFilterCounts();
  }, []);

  // Handle pagination changes
  useEffect(() => {
    getNews(
      filterStatus === "active" ? "1" : 
      filterStatus === "inactive" ? "0" : "",
      filterType
    );
  }, [pageIndex, pageSize, getNews]);

  // Handle global filter changes
  useEffect(() => {
    console.log(globalFilter)
    setPageIndex(0);
    getNews(
      filterStatus === "active" ? "1" : 
      filterStatus === "inactive" ? "0" : "",
      filterType
    );
  }, [globalFilter]);

  return (
    data && (
      <>
        <Typography variant="h4" gutterBottom>
          News
        </Typography>
        <FilterDiv style={{ marginTop: "20px" }}>
          <p
            className={filterStatus === "all" ? "active" : ""}
            onClick={() => filterDataWithStatus("all")}
          >
            All <span>({allData.length})</span>
          </p>
          |
          <p
            className={filterStatus === "active" ? "active" : ""}
            onClick={() => filterDataWithStatus("active")}
          >
            Active <span>({filterActive.length})</span>
          </p>
          |
          <p
            className={filterStatus === "inactive" ? "active" : ""}
            onClick={() => filterDataWithStatus("inactive")}
          >
            {" "}
            Inactive <span>({filterInactive.length})</span>
          </p>
        </FilterDiv>
        <div className={classes.flexRow}>
          <div>
            <TextField
              id="bulk-actions"
              select
              label="Bulk Actions"
              value={bulkActions}
              onChange={handleBulkActions}
              variant="outlined"
              disabled={disabled}
            >
              {actions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              onClick={applyBulkAction}
              disabled={disabled}
            >
              Apply
            </Button>
          </div>
          <div>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to={`${props.match.url}/new`}
              disabled={disabled}
            >
              Add News
            </Button>
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
          </div>
        </div>

        <Paper>
          <Table aria-label="simple table" component="ul" {...getTableProps()}>
            <TableHead component="div">
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableCell
                      {...column.getHeaderProps()}
                      style={{ paddingTop: 6, paddingBottom: 6 }}
                    >
                      {column.render("Header")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody component="div">
              {page.map((row, index) => {
                prepareRow(row);
                const news = row.original;
                return (
                  <TableRow>
                    <TableCell style={{ paddingTop: 6, paddingBottom: 6 }}>
                      <IndeterminateCheckbox
                        {...{ ...row.getToggleRowSelectedProps(), disabled }}
                      />
                    </TableCell>
                    <TableCell
                      style={{
                        paddingTop: 6,
                        paddingBottom: 6,
                        color: "#306F9D",
                      }}
                      component="th"
                      scope="row"
                    >
                      <Link
                        style={{ color: "#306F9D", textDecoration: "none" }}
                        to={`${props.match.url}/${news.id}`}
                      >
                        {news?.localizations?.en?.title}
                      </Link>
                    </TableCell>
                    <TableCell
                      style={{ width: 160, paddingTop: 6, paddingBottom: 6 }}
                    >
                      <Link style={{ color: "#306F9D", textDecoration: "none" }} onClick={(event) => {
                        event.preventDefault();
                        filterByType(news.type);
                        }}>{news.type}</Link>
                    </TableCell>
                    <TableCell
                      style={{ width: 200, paddingTop: 6, paddingBottom: 6 }}
                    >
                      {formatDatetime(news.publishTime)}
                    </TableCell>
                    <TableCell
                      style={{
                        width: 160,
                        paddingTop: 6,
                        paddingBottom: 6,
                        color: "#306F9D",
                      }}
                    >
                     {news.status == "1" ? "Active": "Inactive"}
                    </TableCell>
                    <TableCell
                      style={{ width: 200, paddingTop: 6, paddingBottom: 6 }}
                    >
                      {formatDatetime(news.updateTime)}
                    </TableCell>
                    <TableCell
                      style={{ width: 160, paddingTop: 6, paddingBottom: 6 }}
                    >
                      <Link to={`${props.match.url}/${news.id}`}>
                        <Create fontSize="small" style={{ color: "#CCCCCC" }} />
                      </Link>{" "}
                      {disabled || (
                        <Delete
                          fontSize="small"
                          style={{ color: "#CCCCCC", cursor: "pointer" }}
                          onClick={() => {
                            deletePage(news.id);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>

        <div className={classes.flexRow}>
          <div></div>
          <PaginationBar
            pageSize={pageSize}
            setPageSize={customSetPageSize}
            gotoPage={customGotoPage}
            previousPage={customPreviousPage}
            nextPage={customNextPage}
            canPreviousPage={pageIndex > 0}
            canNextPage={pageIndex < Math.ceil(totalCount / pageSize) - 1}
            pageIndex={pageIndex}
            totalCount={totalCount}
          />
        </div>
      </>
    )
  );
}